// 🔧 use_event_context.js
// ✅ Purpose: Resolve event route context for event-scoped pages
// ✅ Features:
// - First reads from EventContextProvider when available
// - Falls back to direct Firestore loading for older/non-provider usage
// - Preserves existing API: eventslug, eventId, eventDoc, eventLoading, ready
// - Prevents duplicate Firestore reads on event-scoped routes
// - Keeps legacy pages working while architecture is improved
// - Fully commented for clarity

import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase";
import { useEventDoc } from "./use_event_doc";
import { logger } from "../utils/logger";
import { useEventContextValue } from "../context/event_context_provider";

const useEventContextFallback = ({
  redirectIfNotFound = true,
  enabled = true,
} = {}) => {
  const { eventslug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [eventId, setEventId] = useState(null);
  const [slugLoading, setSlugLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const resolveEventFromSlug = async () => {
      // ✅ If a provider is already supplying event context,
      // ✅ this fallback should not perform duplicate Firestore reads.
      if (!enabled) {
        setSlugLoading(false);
        return;
      }

      logger.log("🔄 useEventContext fallback running...", {
        eventslug,
        pathname: location.pathname,
        redirectIfNotFound,
      });

      setEventId(null);

      if (!eventslug) {
        setSlugLoading(false);
        return;
      }

      setSlugLoading(true);

      try {
        const eventQuery = query(
          collection(db, "events"),
          where("slug", "==", eventslug),
          limit(1)
        );

        const eventSnap = await getDocs(eventQuery);

        if (cancelled) return;

        if (eventSnap.empty) {
          logger.warn("⚠️ Event not found for slug:", {
            eventslug,
          });

          if (redirectIfNotFound) {
            navigate("/not-found?eventslug", {
              replace: true,
            });
          }

          return;
        }

        const resolvedEventId = eventSnap.docs[0].id;

        logger.log("🎯 Fallback resolved eventId:", {
          value: resolvedEventId,
        });

        setEventId(resolvedEventId);
      } catch (err) {
        if (cancelled) return;

        logger.error("❌ Failed resolving fallback event context:", {
          value: err,
        });

        if (redirectIfNotFound) {
          navigate("/not-found?eventContextError", {
            replace: true,
          });
        }
      } finally {
        if (!cancelled) {
          setSlugLoading(false);
        }
      }
    };

    resolveEventFromSlug();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    eventslug,
    location.pathname,
    redirectIfNotFound,
    navigate,
  ]);

  const {
    eventDoc,
    loading: eventDocLoading,
  } = useEventDoc(enabled ? eventId : null);

  const eventLoading = enabled
    ? slugLoading || eventDocLoading
    : false;

  return {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    eventError: null,
    ready: !!eventId && !!eventDoc && !eventLoading,
  };
};

export const useEventContext = ({ redirectIfNotFound = true } = {}) => {
  const providerContext = useEventContextValue();

  // ✅ Always initialize fallback hook to keep React hook order stable.
  // ✅ The fallback is disabled when provider context exists.
  const fallbackContext = useEventContextFallback({
    redirectIfNotFound,
    enabled: !providerContext,
  });

  // ✅ Event-scoped routes should use the shared provider.
  if (providerContext) {
    return providerContext;
  }

  // ✅ Non-provider usage still works.
  return fallbackContext;
};